export type InvoiceCollectionStatus =
  "not_issued" | "outstanding" | "partially_collected" | "collected";

/** Invoice totals are one current invoice per registration in the connector. */
export function registrationWaitingBreakdown(
  input: {
    total: number | null;
    completed: number | null;
    pending: number | null;
    cancelled: number | null;
  },
  outstandingInvoiceCount: number | null,
): {
  awaitingPayment: number;
  otherIncomplete: number;
} | null {
  const values = [...Object.values(input), outstandingInvoiceCount];
  if (
    values.some(
      (value) => value === null || !Number.isSafeInteger(value) || value < 0,
    )
  )
    return null;
  const { total, completed, pending, cancelled } = input;
  if (
    completed! + pending! + cancelled! !== total ||
    outstandingInvoiceCount! > pending!
  )
    return null;
  return {
    awaitingPayment: outstandingInvoiceCount!,
    otherIncomplete: pending! - outstandingInvoiceCount!,
  };
}

export interface RegistrationFinanceInput {
  readonly currency: string;
  readonly collectedAmountMinor: number;
  readonly issuedAmountMinor: number;
  readonly outstandingAmountMinor: number;
  readonly collectedInvoiceCount: number;
  readonly outstandingInvoiceCount: number;
}

export interface SponsorshipFinanceInput {
  readonly currency: string;
  readonly contractedAmountMinor: number;
  readonly invoicedAmountMinor: number;
  readonly collectedAmountMinor: number;
  readonly invoiceOutstandingAmountMinor: number;
}

export interface OtherRevenueFinanceInput {
  readonly currency: string;
  readonly amountMinor: number;
  readonly status: "active" | "reversed";
}

export interface ExpenseFinanceInput {
  readonly currency: string;
  readonly amountMinor: number;
  readonly status: "active" | "reversed";
}

export interface ConferenceFinanceInput {
  readonly registration: RegistrationFinanceInput;
  readonly sponsorships: readonly SponsorshipFinanceInput[];
  readonly otherRevenue: readonly OtherRevenueFinanceInput[];
  readonly expenses?: readonly ExpenseFinanceInput[];
}

export interface ConferenceFinanceAggregateInput {
  readonly currency: string;
  readonly registration: Omit<RegistrationFinanceInput, "currency">;
  readonly sponsorshipCollectedAmountMinor: number;
  readonly otherRevenueCollectedAmountMinor: number;
  readonly expenseAmountMinor?: number;
}

export interface InvoiceReconciliation {
  readonly issuedAmountMinor: number;
  readonly collectedAmountMinor: number;
  readonly outstandingAmountMinor: number;
  readonly issuedInvoiceCount: number;
  readonly collectedInvoiceCount: number;
  readonly outstandingInvoiceCount: number;
  readonly status: InvoiceCollectionStatus;
  /** Exact, integer percentage hundredths. 10_000 basis points is 100%. */
  readonly collectionRateBasisPoints: number | null;
}

export interface ConferenceFinanceSummary {
  readonly currency: string;
  readonly registrationCollectedMinor: number;
  readonly sponsorshipCollectedMinor: number;
  readonly otherRevenueCollectedMinor: number;
  readonly totalCollectedMinor: number;
  readonly expenseMinor: number;
  readonly remainingMinor: number;
  readonly invoices: InvoiceReconciliation;
}

export class ConferenceFinanceError extends Error {
  readonly reasonCode:
    | "invalid_currency"
    | "mixed_currency"
    | "invalid_minor_units"
    | "invalid_invoice_count"
    | "registration_invoice_mismatch"
    | "sponsorship_ledger_mismatch"
    | "invalid_other_revenue_status"
    | "invalid_expense_status"
    | "arithmetic_overflow";

  constructor(reasonCode: ConferenceFinanceError["reasonCode"]) {
    super(reasonCode);
    this.name = "ConferenceFinanceError";
    this.reasonCode = reasonCode;
  }
}

const supportedCurrencies = new Set(Intl.supportedValuesOf("currency"));

function currencyCode(value: unknown): string {
  if (
    typeof value !== "string" ||
    !/^[A-Z]{3}$/.test(value) ||
    !supportedCurrencies.has(value)
  ) {
    throw new ConferenceFinanceError("invalid_currency");
  }
  return value;
}

function minorUnits(value: unknown): number {
  if (!Number.isSafeInteger(value) || Number(value) < 0) {
    throw new ConferenceFinanceError("invalid_minor_units");
  }
  return Number(value);
}

function invoiceCount(value: unknown): number {
  if (!Number.isSafeInteger(value) || Number(value) < 0) {
    throw new ConferenceFinanceError("invalid_invoice_count");
  }
  return Number(value);
}

function safeAdd(left: number, right: number): number {
  const sum = left + right;
  if (!Number.isSafeInteger(sum)) {
    throw new ConferenceFinanceError("arithmetic_overflow");
  }
  return sum;
}

function safeSubtract(left: number, right: number): number {
  const difference = left - right;
  if (!Number.isSafeInteger(difference)) {
    throw new ConferenceFinanceError("arithmetic_overflow");
  }
  return difference;
}

function exactBasisPoints(collected: number, issued: number): number | null {
  if (issued === 0) return null;
  const numerator = BigInt(collected) * 10_000n;
  const rounded = (numerator + BigInt(issued) / 2n) / BigInt(issued);
  return Number(rounded);
}

export function reconcileRegistrationInvoices(
  registration: RegistrationFinanceInput,
): InvoiceReconciliation {
  currencyCode(registration.currency);
  const issuedAmountMinor = minorUnits(registration.issuedAmountMinor);
  const collectedAmountMinor = minorUnits(registration.collectedAmountMinor);
  const outstandingAmountMinor = minorUnits(
    registration.outstandingAmountMinor,
  );
  const collectedInvoiceCount = invoiceCount(
    registration.collectedInvoiceCount,
  );
  const outstandingInvoiceCount = invoiceCount(
    registration.outstandingInvoiceCount,
  );
  const issuedInvoiceCount = safeAdd(
    collectedInvoiceCount,
    outstandingInvoiceCount,
  );

  if (
    safeAdd(collectedAmountMinor, outstandingAmountMinor) !==
      issuedAmountMinor ||
    (issuedAmountMinor === 0 && issuedInvoiceCount !== 0) ||
    (issuedAmountMinor > 0 && issuedInvoiceCount === 0) ||
    (outstandingAmountMinor === 0 && outstandingInvoiceCount !== 0) ||
    (outstandingAmountMinor > 0 && outstandingInvoiceCount === 0) ||
    (collectedAmountMinor === 0 && collectedInvoiceCount !== 0) ||
    (collectedAmountMinor === issuedAmountMinor &&
      issuedAmountMinor > 0 &&
      collectedInvoiceCount === 0)
  ) {
    throw new ConferenceFinanceError("registration_invoice_mismatch");
  }

  const status: InvoiceCollectionStatus =
    issuedAmountMinor === 0
      ? "not_issued"
      : outstandingAmountMinor === 0
        ? "collected"
        : collectedAmountMinor === 0
          ? "outstanding"
          : "partially_collected";

  return Object.freeze({
    issuedAmountMinor,
    collectedAmountMinor,
    outstandingAmountMinor,
    issuedInvoiceCount,
    collectedInvoiceCount,
    outstandingInvoiceCount,
    status,
    collectionRateBasisPoints: exactBasisPoints(
      collectedAmountMinor,
      issuedAmountMinor,
    ),
  });
}

function collectedSponsorshipTotal(
  sponsorships: readonly SponsorshipFinanceInput[],
  expectedCurrency: string,
): number {
  let total = 0;
  for (const sponsorship of sponsorships) {
    if (currencyCode(sponsorship.currency) !== expectedCurrency) {
      throw new ConferenceFinanceError("mixed_currency");
    }
    const contracted = minorUnits(sponsorship.contractedAmountMinor);
    const invoiced = minorUnits(sponsorship.invoicedAmountMinor);
    const collected = minorUnits(sponsorship.collectedAmountMinor);
    const invoiceOutstanding = minorUnits(
      sponsorship.invoiceOutstandingAmountMinor,
    );
    if (
      collected > invoiced ||
      invoiced > contracted ||
      invoiceOutstanding !== invoiced - collected
    ) {
      throw new ConferenceFinanceError("sponsorship_ledger_mismatch");
    }
    // Contracted and invoiced values are reconciliation constraints only.
    // Executive collected revenue must include cash collected, never promises.
    total = safeAdd(total, collected);
  }
  return total;
}

function collectedOtherRevenueTotal(
  records: readonly OtherRevenueFinanceInput[],
  expectedCurrency: string,
): number {
  let total = 0;
  for (const record of records) {
    if (currencyCode(record.currency) !== expectedCurrency) {
      throw new ConferenceFinanceError("mixed_currency");
    }
    const amount = minorUnits(record.amountMinor);
    if (record.status !== "active" && record.status !== "reversed") {
      throw new ConferenceFinanceError("invalid_other_revenue_status");
    }
    if (record.status === "active") total = safeAdd(total, amount);
  }
  return total;
}

function activeExpenseTotal(
  records: readonly ExpenseFinanceInput[],
  expectedCurrency: string,
): number {
  let total = 0;
  for (const record of records) {
    if (currencyCode(record.currency) !== expectedCurrency) {
      throw new ConferenceFinanceError("mixed_currency");
    }
    const amount = minorUnits(record.amountMinor);
    if (record.status !== "active" && record.status !== "reversed") {
      throw new ConferenceFinanceError("invalid_expense_status");
    }
    if (record.status === "active") total = safeAdd(total, amount);
  }
  return total;
}

export function buildConferenceFinanceSummary(
  input: ConferenceFinanceInput,
): ConferenceFinanceSummary {
  const currency = currencyCode(input.registration.currency);
  const invoices = reconcileRegistrationInvoices(input.registration);
  const sponsorshipCollectedMinor = collectedSponsorshipTotal(
    input.sponsorships,
    currency,
  );
  const otherRevenueCollectedMinor = collectedOtherRevenueTotal(
    input.otherRevenue,
    currency,
  );
  return financeSummary(
    currency,
    invoices,
    sponsorshipCollectedMinor,
    otherRevenueCollectedMinor,
    activeExpenseTotal(input.expenses ?? [], currency),
  );
}

function financeSummary(
  currency: string,
  invoices: InvoiceReconciliation,
  sponsorshipCollectedMinor: number,
  otherRevenueCollectedMinor: number,
  expenseMinor = 0,
): ConferenceFinanceSummary {
  const registrationCollectedMinor = invoices.collectedAmountMinor;
  const totalCollectedMinor = safeAdd(
    safeAdd(registrationCollectedMinor, sponsorshipCollectedMinor),
    otherRevenueCollectedMinor,
  );

  return Object.freeze({
    currency,
    registrationCollectedMinor,
    sponsorshipCollectedMinor,
    otherRevenueCollectedMinor,
    totalCollectedMinor,
    expenseMinor,
    remainingMinor: safeSubtract(totalCollectedMinor, expenseMinor),
    invoices,
  });
}

/**
 * Builds the same reconciled model from the already-aggregated dashboard API
 * response. Naming is intentionally collected-only: contracted sponsorship
 * value cannot enter this adapter.
 */
export function buildConferenceFinanceSummaryFromAggregate(
  input: ConferenceFinanceAggregateInput,
): ConferenceFinanceSummary {
  const currency = currencyCode(input.currency);
  const invoices = reconcileRegistrationInvoices({
    ...input.registration,
    currency,
  });
  return financeSummary(
    currency,
    invoices,
    minorUnits(input.sponsorshipCollectedAmountMinor),
    minorUnits(input.otherRevenueCollectedAmountMinor),
    minorUnits(input.expenseAmountMinor ?? 0),
  );
}
